/**
 * Dual-Write Storage Adapter
 * P1.3: Writes to both session and Supabase, reads from Supabase first
 * P1.4: Enhanced logging with structured tags, error handling, and fallback
 *
 * Used in Preview environment only for safe testing of Supabase persistence
 * while keeping session as a fallback.
 */

import type { IStorageAdapter } from "../storage-adapter"
import type { Memory } from "@/lib/constants/memory"
import { getSessionAdapter } from "./session-adapter"
import { getSupabaseAdapter } from "./supabase-adapter"

// ============================================================================
// P1.4: Logging helpers (no PII - only counts, field names, IDs)
// ============================================================================

let hasLoggedSupabaseReadError = false
let hasLoggedSupabaseWriteError = false

function logBrainRead(source: "supabase" | "session", count: number, fallback = false): void {
  const prefix = fallback ? "[Brain] READ_FALLBACK" : "[Brain] READ"
  console.log(`${prefix} source=${source} count=${count}`)
}

function logBrainWrite(target: "supabase" | "session", field: string, success: boolean): void {
  console.log(`[Brain] WRITE target=${target} field=${field} success=${success}`)
}

function logBrainError(operation: "read" | "write" | "update" | "delete", target: string, errorCode?: string): void {
  // Only log once per session to avoid noisy logs
  if (operation === "read" && hasLoggedSupabaseReadError) return
  if (operation === "write" && hasLoggedSupabaseWriteError) return

  const tag = operation === "read" ? "supabase_read_error" : "supabase_write_error"
  console.warn(`[Brain] ${tag} operation=${operation} target=${target} code=${errorCode || "unknown"}`)

  if (operation === "read") hasLoggedSupabaseReadError = true
  if (operation === "write") hasLoggedSupabaseWriteError = true
}

// ============================================================================
// Dual-Write Adapter
// ============================================================================

class DualWriteAdapter implements IStorageAdapter {
  readonly name = "dual-write"
  readonly isReadOnly = false

  private sessionAdapter = getSessionAdapter()
  private supabaseAdapter = getSupabaseAdapter()
  private listeners = new Set<() => void>()

  /**
   * Get memories - try Supabase first, fallback to session
   * P1.4: Enhanced with structured logging and single-error-per-session
   */
  async getMemories(includeArchived = false): Promise<Memory[]> {
    try {
      const memories = await this.supabaseAdapter.getMemories(includeArchived)
      logBrainRead("supabase", memories.length)
      return memories
    } catch (error) {
      const errorCode = error instanceof Error ? error.message.slice(0, 50) : "unknown"
      logBrainError("read", "supabase", errorCode)

      // Fallback to session
      const memories = await this.sessionAdapter.getMemories(includeArchived)
      logBrainRead("session", memories.length, true)
      return memories
    }
  }

  /**
   * Get memory by field - try Supabase first, fallback to session
   */
  async getMemoryByField(fieldId: string): Promise<Memory | undefined> {
    try {
      const memory = await this.supabaseAdapter.getMemoryByField(fieldId)
      console.log(`[Brain] READ_FIELD source=supabase field=${fieldId} found=${!!memory}`)
      return memory
    } catch (error) {
      const errorCode = error instanceof Error ? error.message.slice(0, 50) : "unknown"
      logBrainError("read", "supabase", errorCode)

      const memory = await this.sessionAdapter.getMemoryByField(fieldId)
      console.log(`[Brain] READ_FIELD_FALLBACK source=session field=${fieldId} found=${!!memory}`)
      return memory
    }
  }

  /**
   * Save memory - write to both, session first (guaranteed), then Supabase (best effort)
   * P1.4: Session write is guaranteed UX, Supabase is best-effort with logging
   */
  async saveMemory(memory: { field: string; label: string; value: string; section: string }): Promise<Memory> {
    // Always write to session first (guaranteed to work)
    const savedMemory = await this.sessionAdapter.saveMemory(memory)
    logBrainWrite("session", memory.field, true)

    // Then try Supabase (best effort)
    try {
      await this.supabaseAdapter.saveMemory(memory)
      logBrainWrite("supabase", memory.field, true)
    } catch (error) {
      const errorCode = error instanceof Error ? error.message.slice(0, 50) : "unknown"
      logBrainError("write", "supabase", errorCode)
      logBrainWrite("supabase", memory.field, false)
    }

    this.notifyListeners()
    return savedMemory
  }

  /**
   * Update memory - update both, session first
   */
  async updateMemory(id: string, updates: Partial<Memory>): Promise<void> {
    // Always update session first
    await this.sessionAdapter.updateMemory(id, updates)
    console.log(`[Brain] UPDATE target=session id=${id.slice(0, 8)}... success=true`)

    // Then try Supabase
    try {
      await this.supabaseAdapter.updateMemory(id, updates)
      console.log(`[Brain] UPDATE target=supabase id=${id.slice(0, 8)}... success=true`)
    } catch (error) {
      const errorCode = error instanceof Error ? error.message.slice(0, 50) : "unknown"
      logBrainError("update", "supabase", errorCode)
    }

    this.notifyListeners()
  }

  /**
   * Delete memory - delete from both
   */
  async deleteMemory(id: string): Promise<void> {
    // Always delete from session first
    await this.sessionAdapter.deleteMemory(id)
    console.log(`[Brain] DELETE target=session id=${id.slice(0, 8)}... success=true`)

    // Then try Supabase
    try {
      await this.supabaseAdapter.deleteMemory(id)
      console.log(`[Brain] DELETE target=supabase id=${id.slice(0, 8)}... success=true`)
    } catch (error) {
      const errorCode = error instanceof Error ? error.message.slice(0, 50) : "unknown"
      logBrainError("delete", "supabase", errorCode)
    }

    this.notifyListeners()
  }

  /**
   * Archive memory
   */
  async archiveMemory(id: string): Promise<void> {
    await this.updateMemory(id, { isArchived: true })
  }

  /**
   * Restore memory
   */
  async restoreMemory(id: string): Promise<void> {
    await this.updateMemory(id, { isArchived: false })
  }

  /**
   * Get key details completed - try Supabase first
   */
  async getKeyDetailsCompleted(): Promise<number> {
    try {
      const count = await this.supabaseAdapter.getKeyDetailsCompleted()
      console.log(`[Brain] KEY_DETAILS source=supabase count=${count}`)
      return count
    } catch (error) {
      const count = await this.sessionAdapter.getKeyDetailsCompleted()
      console.log(`[Brain] KEY_DETAILS_FALLBACK source=session count=${count}`)
      return count
    }
  }

  /**
   * Clear all memories from both stores
   */
  async clearAll(): Promise<void> {
    await this.sessionAdapter.clearAll()
    console.log("[Brain] CLEAR target=session success=true")

    try {
      await this.supabaseAdapter.clearAll()
      console.log("[Brain] CLEAR target=supabase success=true")
    } catch (error) {
      console.warn("[Brain] CLEAR target=supabase success=false")
    }

    this.notifyListeners()
  }

  /**
   * Subscribe to changes
   */
  subscribe(callback: () => void): () => void {
    this.listeners.add(callback)

    // Subscribe to both adapters
    const unsubSession = this.sessionAdapter.subscribe(callback)
    // Note: Supabase adapter doesn't have real-time subscription yet

    return () => {
      this.listeners.delete(callback)
      unsubSession()
    }
  }

  private notifyListeners(): void {
    this.listeners.forEach((l) => l())
  }
}

// ============================================================================
// Export
// ============================================================================

let dualWriteInstance: DualWriteAdapter | null = null

export function getDualWriteAdapter(): IStorageAdapter {
  if (!dualWriteInstance) {
    dualWriteInstance = new DualWriteAdapter()
  }
  return dualWriteInstance
}

/**
 * Check if dual-write mode should be enabled
 * Only enabled in Preview (Vercel) and when the flag is set
 */
export function isDualWriteModeEnabled(): boolean {
  if (typeof window === "undefined") return false

  // Check for Vercel preview environment
  const isVercelPreview = process.env.NEXT_PUBLIC_VERCEL_ENV === "preview"

  // Check for the feature flag (separate from full Supabase mode)
  const flagEnabled = process.env.NEXT_PUBLIC_BIZIQ_USE_SUPABASE_BRAIN === "true"

  // Dual-write only in preview with flag
  return isVercelPreview && flagEnabled
}

/**
 * P1.4: Reset error logging flags (for testing)
 */
export function resetBrainErrorFlags(): void {
  hasLoggedSupabaseReadError = false
  hasLoggedSupabaseWriteError = false
}
